import { Utils } from '@client/utility';
import { useUserStore } from '@client/store';
import { GetUserUUID } from '@client/components';
import { ModelIdentifier } from '@client/store'; 

function mapPlanToTier(plan: string): 'free' | 'premium' {
  return plan === 'Paid' ? 'premium' : 'free';
}

export async function GetUserData(): Promise<any> {
  const user_uuid = await GetUserUUID();
  if (!user_uuid) {
    console.warn('Cannot fetch user data: User not authenticated');
    return { error: 'User not authenticated. Please log in to continue.' };
  }

  try {
    const response = await Utils.post<any>(
      '/api/GetUserData',
      { user_uuid },
      { cache: true }
    );

    if (!response || typeof response.username === 'undefined') {
      console.error('Invalid response from GetUserData API:', response);
      return { error: 'Failed to fetch user data from server' };
    }

    const store = useUserStore.getState();

    store.setUserUUID(user_uuid);
    store.setUsername(response.username);
    store.setPersonaName(response.persona_name || response.username);
    store.setUserIcon(response.user_avatar);
    store.setIsAdmin(response.is_admin);
    store.setCredits(response.balance || 0);
    store.setTier(response.tier || 'FREE');
    store.setPreferredModel(response.preferred_model?.slug || '');

    if (response.ai_customization) {
      store.setInferenceSettings(response.ai_customization);
    }

    // Map public models with proper typing
    const publicModels: ModelIdentifier[] = Object.entries(response.tier_data.model).map(
      ([slug, info]: [string, any]) => ({
        name: info.name || info.slug,
        model_description: info.description,
        slug: info.slug,
        is_selected: info.is_selected,
        tier: mapPlanToTier(info.plan),
      })
    );

    const privateModels: ModelIdentifier[] = Object.entries(
      response.tier_data.private_models || {}
    ).map(([key, info]: [string, any]) => ({
      name: info.model_name,
      model_description: info.model_description,
      slug: key,
      is_selected: info.is_selected,
      tier: 'premium' as const,
    }));

    store.setModelIdentifiers([...publicModels, ...privateModels]);
    store.setMaxTokenLimit(response.tier_data.max_token);

    return response;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return { error: 'An error occurred while fetching user data' };
  }
}